import React from "react";
import ReactDOM from "react-dom/client";
import {
  ApolloClient,
  ApolloProvider,
  createHttpLink,
  InMemoryCache,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import {
  decodeToken,
  isTokenValid,
  LogLevel,
  serviceLineMapper,
  updateTokeninSessionStorage,
  WidgetConfig,
  WidgetOptions,
} from "./utils/common";
import { createTheme, CssBaseline, ThemeProvider } from "@mui/material";
import App from "./App";
import theme from "./theme/Theme";
import { getPermissionPolicies } from "./queries/PermissionPolicies";
import { policiesContext } from "./utils/common";
import jwt from "jwt-decode";
import { SnackbarProvider } from "./context/AlertsContext";
import { getCoderAssignmentConfig } from "./queries/configuration";

class ServiceDesk {
  theme: any;
  bearerToken: string = "";
  config: WidgetConfig;
  logLevel: LogLevel;
  apolloClient: ApolloClient<any>;
  et: EventTarget;

  constructor(config: WidgetConfig) {
    this.config = config;
    this.logLevel = config?.logLevel || LogLevel.ERROR;
    if ("theme" in config) {
      const defaultTheme = createTheme();
      let newTheme = config.theme;
      if ("palette" in config.theme) {
        Object.keys(config.theme.palette).forEach((prop) => {
          // newTheme.palette[prop] = defaultTheme.palette.augmentColor({
          //   color: {
          //     main: config.theme.palette[prop],
          //   },
          // });
          newTheme.palette[prop] = {
            main: config.theme.palette[prop],
          };
        });
      }
      this.theme = createTheme(theme, newTheme);
    } else {
      this.theme = theme;
    }
    this.et = new EventTarget();
    config
      .getBearerToken()
      .then((bearerToken) => {
        this.bearerToken = bearerToken;
        this.initApolloClient();
      })
      .catch((err) => {
        console.error("Failed to receive bearer token.", err);
      });
  }

  static init(config: any): ServiceDesk {
    const xcaliberWidget = new ServiceDesk(config);
    return xcaliberWidget;
  }

  initApolloClient() {
    const httpLink = createHttpLink({
      uri: this.config.bffURL ?? process.env.REACT_APP_BFF_SERVER,
    });
    const authLink = setContext((_, { headers }) => {
      return {
        headers: {
          ...headers,
          authorization: this.bearerToken,
        },
      };
    });
    this.apolloClient = new ApolloClient({
      link: authLink.concat(httpLink),
      cache: new InMemoryCache(),
    });
    const event = document.createEvent("Event");
    event.initEvent("ready", true, true);
    this.et.dispatchEvent(event);
  }

  async refreshToken() {
    this.apolloClient = null;
    const token = await this.config.getBearerToken();
    this.bearerToken = token;
    this.initApolloClient();
    updateTokeninSessionStorage(token);
    return token;
  }

  async renderWidget(
    widgetName: string,
    elementId: string,
    apolloClient: any,
    widgetOptions?: WidgetOptions
  ) {
    let policies = [];
    let coderAssignmentConfig = [];
    try {
      const [policiesResponse, coderAssignmentConfigResponse] = await Promise.all([
        this.apolloClient?.query({
          query: getPermissionPolicies,
          fetchPolicy: "no-cache",
        }),
        this.apolloClient?.query({
          query: getCoderAssignmentConfig,
          variables: {
            service:
              serviceLineMapper[widgetOptions?.userContext?.serviceLine?.name] ??
              widgetOptions?.userContext?.serviceLine?.name,
          },
          fetchPolicy: "cache-first",
        }),
      ]);
    
      policies = policiesResponse?.data?.PermissionPolicies?.data;
      coderAssignmentConfig = coderAssignmentConfigResponse?.data?.CoderAssignmentConfig;
    
    } catch (err) {
      console.error("Failed to fetch policies and coder assignment config:", err);
    }

    const decodedPayload: any = jwt(this.bearerToken);
    if (!this.apolloClient) {
      this.et.addEventListener("ready", () => {
        const root = ReactDOM.createRoot(
          document.getElementById(elementId) as HTMLElement
        );
        sessionStorage.setItem(
          "tokenPayload",
          JSON.stringify(decodeToken(this.bearerToken))
        );
        updateTokeninSessionStorage(this.bearerToken);
        root.render(
          // <React.StrictMode>
          <ApolloProvider client={this.apolloClient}>
            <ThemeProvider theme={this.theme}>
              <CssBaseline />
              <policiesContext.Provider
                value={{
                  policies,
                  roles: decodedPayload?.roles,
                  userContext: widgetOptions?.userContext,
                  coderAssignmentConfig,
                }}
              >
                <App
                  token={decodeToken(this.bearerToken)}
                  widgetName={widgetName}
                  client={apolloClient}
                  widgetOptions={widgetOptions}
                />
              </policiesContext.Provider>
            </ThemeProvider>
          </ApolloProvider>
          // </React.StrictMode>
        );
      });
    } else {
      const root = ReactDOM.createRoot(
        document.getElementById(elementId) as HTMLElement
      );
      sessionStorage.setItem(
        "tokenPayload",
        JSON.stringify(decodeToken(this.bearerToken))
      );
      updateTokeninSessionStorage(this.bearerToken);
      root.render(
        <React.StrictMode>
          <ApolloProvider client={this.apolloClient}>
            <ThemeProvider theme={this.theme}>
              <CssBaseline />
              <policiesContext.Provider
                value={{
                  policies,
                  roles: decodedPayload?.roles,
                  userContext: widgetOptions?.userContext,
                  coderAssignmentConfig,
                }}
              >
                <SnackbarProvider>
                  <App
                    token={decodeToken(this.bearerToken)}
                    widgetName={widgetName}
                    client={apolloClient}
                    widgetOptions={widgetOptions}
                  />
                </SnackbarProvider>
              </policiesContext.Provider>
            </ThemeProvider>
          </ApolloProvider>
        </React.StrictMode>
      );
    }
  }

  create(widgetName: string, elementId: string, widgetOptions?: WidgetOptions) {
    if (!isTokenValid(this.bearerToken)) {
      this.refreshToken()
        .then((token) => {
          if (isTokenValid(token)) {
            this.renderWidget(
              widgetName,
              elementId,
              this.apolloClient,
              widgetOptions
            );
          } else {
            throw new Error("Received invalid token on refresh");
          }
        })
        .catch((err) => {
          console.error(`Failed to refresh Xcaliber bearer token - ${err}`);
        });
    } else {
      this.renderWidget(widgetName, elementId, this.apolloClient);
    }
  }
}

export const init = ServiceDesk.init;

export default ServiceDesk;
